<!--
  CampaignTaxLegal.vue — Claude Design redesign experiment.

  Tax & Legal section in the redesigned editor sidebar (Operations group).

  Surfaces:
   - Distributor nexus states (read-only, with deep link to the
     distributor profile page where they can be edited)
   - Tax-related campaign fields surfaced from CampaignCheckoutInfo /
     CampaignPaymentOptions (collect_sales_tax, tax_exempt, etc.) — these
     remain editable here, mirroring values via v-model on the campaign
     prop
   - Placeholder for future legal/disclaimer fields (Terms & Conditions,
     etc. — not yet a product feature, but the section reserves the
     space)

  Why this section exists separately from Checkout Fields: the legacy
  "Tax & Legal" sidebar entry pointed at CampaignCheckoutInfo, which
  is actually the checkout-form-field configurator (First Name, Last
  Name, Department, etc.). Renaming that section to "Checkout Fields"
  freed the "Tax & Legal" name for content that's actually about taxes
  and legal — this component.

  Read-only nexus chips honor the data-ownership boundary: nexus_states
  is a per-distributor field, not per-campaign. Editing it here would
  silently change tax behavior for every campaign of that distributor.
  Per Issue 5 / option β: read-only chip list + "Edit on distributor"
  deep link.
-->
<template>
  <div class="ctl" data-testid="ctl-root">
    <editor-card title="Sales Tax Nexus">
      <p class="ctl__sub">
        Sales tax nexus is the set of states where
        <strong v-if="distributorName">{{ distributorName }}</strong>
        <strong v-else>the distributor</strong>
        has a sales tax obligation — usually because of physical presence,
        employees, inventory, or economic-nexus thresholds.
        It's owned by the distributor and inherited by every store on
        their account, so it can't be edited here.
        Stores will collect tax on shipments to these states (when the
        Collect Sales Tax toggle below is on).
      </p>

      <div class="ctl__nexus-label">Inherited from distributor</div>
      <div class="ctl__nexus" v-if="nexusStates && nexusStates.length">
        <span
          v-for="state in nexusStates"
          :key="state"
          class="ctl__chip ctl__chip--outlined-blue"
          data-testid="ctl-nexus-chip"
        >
          {{ state }}
        </span>
      </div>
      <div v-else class="ctl__nexus-empty">
        <span class="tf-icon">info</span>
        <span>
          No nexus states are configured for
          <strong v-if="distributorName">{{ distributorName }}</strong>
          <strong v-else>this distributor</strong>
          yet — until that's set, no sales tax will be collected on this store.
        </span>
      </div>

      <div class="ctl__nexus-link" v-if="distributorId">
        <span class="tf-icon">edit</span>
        <router-link :to="`/distributor/account/compliance/tax-nexus`" class="ctl__link">
          {{ distributorName ? `Edit nexus on ${distributorName}` : 'Edit on distributor profile' }}
        </router-link>
      </div>
    </editor-card>

    <editor-card title="Tax Behavior">
      <p class="ctl__sub">
        Per-store tax handling. These settings determine how sales tax is
        calculated and displayed at checkout.
      </p>

      <!--
        Collect Sales Tax and Consolidate Tax & Fees are owned by the Pricing
        section's Payments card (StorePaymentOptions), which keeps its own
        copy and re-applies it on every autosave. A second switch here wrote
        the campaign directly and was reverted by the next autosave (Bug
        Intake #516), so there is ONE control and this row points to it.
        Tax Exempt has no other home and stays here.
      -->
      <div class="ctl__row" data-testid="ctl-tax-lives-on-pricing">
        <div class="ctl__row-label">
          <div class="ctl__row-title">Collect Sales Tax</div>
          <div class="ctl__row-help">
            Whether sales tax is added at checkout, and how it is shown, is set
            on the Pricing section's payments card, for every payment method.
            <router-link v-if="pricingLink" :to="pricingLink" class="ctl__link">
              Open Pricing
            </router-link>
          </div>
        </div>
      </div>

      <div class="ctl__row">
        <div class="ctl__row-label">
          <div class="ctl__row-title">Tax Exempt</div>
          <div class="ctl__row-help">
            Mark this store as tax-exempt. Overrides the Collect Sales Tax
            setting; no tax will be calculated regardless.
          </div>
        </div>
        <v-switch
          data-testid="ctl-tax-exempt"
          :model-value="!!campaign.tax_exempt"
          @update:modelValue="onToggleTaxExempt"
          color="primary"
          density="compact"
          hide-details
        />
      </div>
    </editor-card>

    <editor-card title="Privacy & refund policies">
      <p class="ctl__sub">
        The Privacy and Refund policy links in your store's footer. For each
        one, link to a page you host elsewhere, or write the policy here and
        ThreadFlow shows it on its own page inside your store.
      </p>

      <store-policy-editor
        kind="privacy"
        :campaign="campaign"
        :edit-mode="editMode"
        @campaign-changed="$emit('campaign-changed')"
      />
      <store-policy-editor
        kind="refund"
        :campaign="campaign"
        :edit-mode="editMode"
        @campaign-changed="$emit('campaign-changed')"
      />
    </editor-card>
  </div>
</template>

<script>
import { canonicalNexusNames } from "@/data/usStates";
import EditorCard from "./components/EditorCard.vue";
import StorePolicyEditor from "./components/StorePolicyEditor.vue";

export default {
  name: "StoreTaxLegal",
  components: {
    EditorCard,
    StorePolicyEditor,
  },
  props: {
    campaign: { type: Object, required: true },
    editMode: { type: Boolean, default: false },
  },
  emits: ['campaign-changed', 'update-tab-invalid'],
  computed: {
    distributorId() {
      return this.campaign?.distributor?.id || this.campaign?.distributor_id || null;
    },
    distributorName() {
      return this.campaign?.distributor?.name || null;
    },
    pricingLink() {
      const id = this.campaign?._jv?.id || this.campaign?.id;
      return id ? `/stores/${id}/edit/pricing` : null;
    },
    nexusStates() {
      // Distributor.nexus_states may be an array, a comma-separated
      // string, or null. Normalize to a sorted array of FULL state names,
      // one per state: legacy rows may carry a 2-letter code ("OR") beside
      // or instead of the name ("Oregon"); card #237.
      const raw = this.campaign?.distributor?.nexus_states;
      if (!raw) return [];
      if (Array.isArray(raw)) return canonicalNexusNames(raw);
      if (typeof raw === 'string') {
        return canonicalNexusNames(
          raw.split(',').map(s => s.trim()).filter(Boolean)
        );
      }
      return [];
    },
  },
  methods: {
    onToggleTaxExempt(val) {
      this.campaign.tax_exempt = val;
      this.$emit('campaign-changed');
    },
    // Tab-style validate / save methods so this section participates in
    // the same incompleteTabs / saveAndExit / autosave loop as the
    // legacy tabs. Currently no validation rules — tax fields are
    // independently valid by toggle. Returns false (= valid).
    validateTab() { return false; },
    updateCampaign(campaign) { return campaign; },
  },
};
</script>

<style scoped>
.ctl { font-family: var(--tf-font-sans, 'Roboto', sans-serif); }

.ctl__sub {
  font: 400 13px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-fg-4, var(--tf-fg-4));
  margin: 0 0 18px;
  line-height: 1.5;
}

.ctl__nexus-label {
  font: 500 11px var(--tf-font-sans, 'Roboto', sans-serif);
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: var(--tf-fg-4, var(--tf-fg-4));
  margin-bottom: 8px;
}
.ctl__nexus {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
}
.ctl__chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: var(--tf-surface-dark, var(--tf-surface-dark));
  color: var(--tf-secondary, var(--tf-secondary));
  border-radius: 9999px;
  font: 500 12px var(--tf-font-sans, 'Roboto', sans-serif);
  letter-spacing: 0.4px;
}
/* Outlined-blue chip variant (PR B3, store editor design parity).
   Per the 2026-05-29 handoff, nexus chips read as state pills with a
   primary-color border and tinted background -- not flat secondary
   chips. The variant lives next to the base ``.ctl__chip`` so the
   selector specificity remains predictable. */
.ctl__chip--outlined-blue {
  background: var(--tf-primary-soft, rgba(25, 118, 210, 0.08));
  color: var(--tf-primary, #1976d2);
  border: 1px solid var(--tf-primary, #1976d2);
}
.ctl__nexus-empty,
.ctl__placeholder-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: var(--tf-surface-light, var(--tf-surface-light));
  border-radius: 6px;
  font: 400 13px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-fg-4, var(--tf-fg-4));
  margin-bottom: 14px;
}
.ctl__nexus-empty .tf-icon,
.ctl__placeholder-empty .tf-icon {
  font-size: 18px;
  color: var(--tf-fg-4, var(--tf-fg-4));
}

.ctl__nexus-link {
  display: flex;
  align-items: center;
  gap: 6px;
  font: 400 13px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-fg-3, var(--tf-fg-3));
}
.ctl__nexus-link .tf-icon {
  font-size: 16px;
  color: var(--tf-fg-4, var(--tf-fg-4));
}
.ctl__link {
  color: var(--tf-primary, var(--tf-primary));
  font-weight: 500;
  text-decoration: none;
}
.ctl__link:hover { text-decoration: underline; }
.ctl__hint { color: var(--tf-fg-4, var(--tf-fg-4)); }

.ctl__row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 12px 0;
  border-top: 1px solid var(--tf-border, var(--tf-border));
}
.ctl__row:first-of-type { border-top: none; }
.ctl__row-label { flex: 1; }
.ctl__row-title {
  font: 500 14px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-fg-1, var(--tf-fg-1));
  margin-bottom: 4px;
}
.ctl__row-help {
  font: 400 12px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-fg-4, var(--tf-fg-4));
  line-height: 1.5;
  max-width: 480px;
}

/* PR B3 -- Legal Copy card field stack. */
.ctl__legal-field {
  margin-bottom: 16px;
}
.ctl__legal-field:last-of-type {
  margin-bottom: 0;
}
.ctl__legal-label {
  display: block;
  font: 500 13px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-fg-1, var(--tf-fg-1));
  margin-bottom: 6px;
}
.ctl__legal-help {
  font: 400 12px var(--tf-font-sans, 'Roboto', sans-serif);
  color: var(--tf-fg-4, var(--tf-fg-4));
  margin: 6px 0 0;
}
</style>
